import { useCallback, useEffect, useState } from "react";

import { Icon } from "./Icon.js";
import { CheckBox } from "./Toggle.js";
import { ApiError } from "../api/client.js";
import { formatWhen } from "../alarm-view.js";
import { useI18n } from "../i18n.js";
import { useSession } from "../session.js";

/**
 * La scheda BACKUP E RIPRISTINO, portata da `SettingsBackupTab.qml`.
 *
 * Tre cose: la copia manuale, la copia **programmata su FTP** e lo storico.
 * La seconda e' quella che conta su un impianto — una copia che qualcuno deve
 * ricordarsi di fare e' una copia che a un certo punto non c'e'.
 *
 * Due differenze dall'app desktop, dichiarate:
 *
 *  - la **password FTP non torna indietro**. Il server dice solo se e'
 *    impostata: una credenziale che non attraversa la rete non puo' finire in
 *    un log del browser o in uno screenshot.
 *  - il **ripristino non si fa da qui**. Sostituire il database sotto un
 *    processo che sta sorvegliando sei impianti lascerebbe i cicli di lettura
 *    su un file che non esiste piu'. Il bottone resta, spento, col motivo.
 */

interface BackupRow {
  filename: string;
  sizeBytes: number;
  createdAt: string;
}

interface FtpStatus {
  host: string;
  port: number;
  user: string;
  passwordSet: boolean;
  remotePath: string;
  secure: boolean;
  enabled: boolean;
  intervalHours: number;
  nextRun?: string;
  lastRun?: string;
  lastResult?: string;
  lastError?: string;
  schedulerActive?: boolean;
}

export function BackupTab() {
  const { t } = useI18n();
  const { api, can } = useSession();

  const [rows, setRows] = useState<BackupRow[]>([]);
  const [ftp, setFtp] = useState<FtpStatus | undefined>();
  const [password, setPassword] = useState("");
  const [siteName, setSiteName] = useState("");
  const [busy, setBusy] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState("");
  const [denied, setDenied] = useState(false);

  const fail = useCallback((e: unknown) => {
    if (e instanceof ApiError && e.isForbidden) setDenied(true);
    else setError(e instanceof ApiError ? e.message : String(e));
  }, []);

  const reload = useCallback(async () => {
    try {
      setRows(await api.call<BackupRow[]>("backup.list"));
      setError("");
      setDenied(false);
    } catch (e) {
      fail(e);
    }
    try {
      setFtp(await api.call<FtpStatus>("ftp.status"));
    } catch {
      /* la sezione FTP richiede manageSystem: senza, resta chiusa */
    }
    try {
      const s = await api.call<Record<string, string>>("settings.list");
      setSiteName(s["site_name"] ?? "");
    } catch {
      /* il nome del sito e' un di piu' */
    }
  }, [api, fail]);

  useEffect(() => void reload(), [reload]);

  const run = async (id: string, fn: () => Promise<unknown>, okNote = "") => {
    setBusy(id);
    setNote("");
    try {
      await fn();
      if (okNote) setNote(okNote);
      await reload();
    } catch (e) {
      fail(e);
    } finally {
      setBusy("");
    }
  };

  if (denied) {
    return (
      <div className="settings__pane">
        <p className="muted">{t("users.superAdminRequired")}</p>
      </div>
    );
  }

  const last = rows[0];

  return (
    <div className="settings__pane analytics">
      {error && <p className="alert">{error}</p>}
      {note && <p className="ok">{note}</p>}

      <div className={`ahero ahero--${last ? "normal" : "stale"}`}>
        <span className="ahero__dot">
          <i />
        </span>
        <div className="ahero__text">
          <h3>{t("backup.title")}</h3>
          <p className="muted small">
            {last
              ? `${t("backup.lastBackup")}: ${formatWhen(last.createdAt)} · ${t("backup.totalBackups")}: ${rows.length}`
              : t("backup.noBackups")}
          </p>
        </div>
        <button
          className="btn--primary"
          disabled={busy !== ""}
          onClick={() => void run("create", () => api.call("backup.create"))}
        >
          {busy === "create" ? t("backup.creating") : t("backup.createBackup")}
        </button>
      </div>

      <p className="muted small">{t("backup.description")}</p>

      <h3 className="admin__title">{t("backup.siteName")}</h3>
      <div className="panel">
        <p className="muted small">{t("backup.siteNameDesc")}</p>
        <div className="ptz__row">
          <input
            value={siteName}
            placeholder={t("backup.siteNamePlaceholder")}
            onChange={(e) => setSiteName(e.target.value)}
          />
          <button
            onClick={() => void run("site", () => api.call("settings.update", { site_name: siteName }))}
          >
            {t("settings.save")}
          </button>
        </div>
      </div>

      {ftp && can("manageSystem") && (
        <>
          <h3 className="admin__title">{t("ftp.title")}</h3>
          <div className="panel">
            <p className="muted small">{t("ftp.description")}</p>

            <div className="cgrid">
              <label className="cfield">
                <span>{t("ftp.host")}</span>
                <input value={ftp.host} onChange={(e) => setFtp({ ...ftp, host: e.target.value })} />
              </label>
              <label className="cfield">
                <span>{t("ftp.port")}</span>
                <input
                  type="number"
                  value={ftp.port}
                  onChange={(e) => setFtp({ ...ftp, port: Number(e.target.value) })}
                />
              </label>
              <label className="cfield">
                <span>{t("ftp.user")}</span>
                <input value={ftp.user} onChange={(e) => setFtp({ ...ftp, user: e.target.value })} />
              </label>
              <label className="cfield">
                <span>
                  {t("ftp.password")}
                  {ftp.passwordSet ? " ✓" : ""}
                </span>
                {/* Vuoto = lascia quella che c'e'. Il server non la manda
                    indietro, quindi non c'e' niente da riscrivere per sbaglio. */}
                <input
                  type="password"
                  value={password}
                  autoComplete="new-password"
                  onChange={(e) => setPassword(e.target.value)}
                />
              </label>
              <label className="cfield">
                <span>{t("ftp.interval")}</span>
                <input
                  type="number"
                  min={1}
                  value={ftp.intervalHours}
                  onChange={(e) => setFtp({ ...ftp, intervalHours: Number(e.target.value) })}
                />
              </label>
              <label className="cfield cfield--wide">
                <span>{t("ftp.remotePath")}</span>
                <input
                  value={ftp.remotePath}
                  onChange={(e) => setFtp({ ...ftp, remotePath: e.target.value })}
                />
              </label>
              <span className="cfield cfield--toggle">
                <CheckBox
                  on={ftp.secure}
                  onChange={(v) => setFtp({ ...ftp, secure: v })}
                  label={t("ftp.secure")}
                />
              </span>
              <span className="cfield cfield--toggle">
                <CheckBox
                  on={ftp.enabled}
                  onChange={(v) => setFtp({ ...ftp, enabled: v })}
                  label={t("ftp.enabled")}
                />
              </span>
            </div>

            <div className="ptz__row">
              <button
                className="btn--primary"
                disabled={busy !== ""}
                onClick={() =>
                  void run(
                    "ftp",
                    () =>
                      api.call("settings.update", {
                        ftp_host: ftp.host,
                        ftp_port: String(ftp.port),
                        ftp_user: ftp.user,
                        ftp_remote_path: ftp.remotePath,
                        ftp_secure: ftp.secure ? "true" : "false",
                        ftp_enabled: ftp.enabled ? "true" : "false",
                        ftp_interval_hours: String(Math.max(1, ftp.intervalHours || 24)),
                        ...(password ? { ftp_password: password } : {}),
                      }),
                    t("ftp.saved"),
                  )
                }
              >
                {busy === "ftp" ? t("ftp.saving") : t("ftp.saveSettings")}
              </button>
              <button
                disabled={busy !== ""}
                onClick={() => void run("test", () => api.call("ftp.test"), t("ftp.testSuccess"))}
              >
                {busy === "test" ? t("ftp.testing") : t("ftp.testConnection")}
              </button>
              {/* "Invia adesso" fabbrica una copia nuova e manda quella: la
                  rotta vuole il nome del file, e spedire l'ultima che c'era
                  vorrebbe dire mandare qualcosa di vecchio. */}
              <button
                disabled={busy !== "" || !last}
                onClick={() =>
                  void run(
                    "now",
                    async () => {
                      const b = await api.call<{ filename: string }>("backup.create");
                      await api.call("ftp.backup_now", { filename: b.filename });
                    },
                    t("ftp.success"),
                  )
                }
              >
                {busy === "now" ? t("ftp.sending") : t("ftp.backupNow")}
              </button>
            </div>

            <div className="row">
              <span className="muted">{t("ftp.schedulerActive")}</span>
              <span className={ftp.schedulerActive ? "ok" : "muted"}>
                {ftp.schedulerActive ? t("ftp.yes") : t("ftp.no")}
              </span>
            </div>
            <div className="row">
              <span className="muted">{t("ftp.interval")}</span>
              <span className="mono">
                {ftp.intervalHours}h
                {ftp.nextRun ? ` · ${formatWhen(ftp.nextRun)}` : ""}
              </span>
            </div>
            <div className="row">
              <span className="muted">{t("ftp.lastBackup")}</span>
              <span className="mono">{ftp.lastRun ? formatWhen(ftp.lastRun) : t("ftp.never")}</span>
            </div>
            <div className="row">
              <span className="muted">{t("ftp.lastResult")}</span>
              <span className={ftp.lastResult === "ok" ? "ok" : ftp.lastResult ? "ko" : "muted"}>
                {ftp.lastResult || "—"}
                {ftp.lastError ? ` · ${ftp.lastError}` : ""}
              </span>
            </div>
          </div>
        </>
      )}

      <h3 className="admin__title">{t("backup.title")}</h3>
      <div className="atable">
        <table className="table">
          <thead>
            <tr>
              <th>{t("settings.name")}</th>
              <th className="ta-right">{t("backup.fileSize")}</th>
              <th>{t("backup.createdAt")}</th>
              <th className="ta-right">{t("alarm.audit.action")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((b) => (
              <tr key={b.filename}>
                <td className="mono">{b.filename}</td>
                <td className="ta-right mono">{formatBytes(b.sizeBytes)}</td>
                <td className="muted">{formatWhen(b.createdAt)}</td>
                <td className="ta-right row__actions">
                  <a className="link" href={`/api/backup/file/${encodeURIComponent(b.filename)}`}>
                    <Icon name="download" size={14} />
                  </a>
                  {/* Il ripristino c'e' e resta spento: si fa a servizio
                      fermo, non da una pagina web. */}
                  <button className="link" disabled title={t("backup.confirmRestore")}>
                    {t("backup.restore")}
                  </button>
                  <button
                    className="link"
                    onClick={() => {
                      if (!window.confirm(t("backup.confirmDelete"))) return;
                      void run("del", () => api.call("backup.remove", { filename: b.filename }));
                    }}
                  >
                    {t("backup.delete")}
                  </button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={4} className="muted">
                  {t("backup.noBackups")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
